import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BsArrowRight,
  BsBoxArrowUpRight,
  BsCreditCard2Front,
  BsHouseDoor,
  BsLayers,
  BsLink45Deg,
  BsSearch,
  BsShieldLock,
} from 'react-icons/bs';
import logo from '../../assets/logo.png';
import ApiDocumentation, {
  NAV_SECTIONS,
  type NavSectionId,
} from '../../pages/api-documentation/ApiDocumentation';
import './PublicDocsPage.css';

type SidebarLink = {
  id: string;
  label: string;
  target: 'home' | NavSectionId;
  icon?: React.ComponentType;
};

const SIDEBAR_GROUPS: { title?: string; links: SidebarLink[] }[] = [
  {
    links: [{ id: 'home', label: 'Home', target: 'home', icon: BsHouseDoor }],
  },
  {
    title: 'Getting started',
    links: [
      { id: 'overview', label: 'Overview', target: 'overview' },
      { id: 'credentials', label: 'Credentials', target: 'credentials' },
      { id: 'headers', label: 'Required headers', target: 'headers' },
      { id: 'signature', label: 'Request signing', target: 'signature' },
    ],
  },
  {
    title: 'Endpoints',
    links: [
      { id: 'process-payment', label: 'Process payment', target: 'process-payment' },
      { id: 'status-webhook', label: 'Status webhook', target: 'status-webhook' },
      { id: 'checkout', label: 'Hosted checkout', target: 'checkout' },
      { id: 'iframe', label: 'Iframe embed', target: 'iframe' },
      { id: 'transaction-status', label: 'Transaction status', target: 'transaction-status' },
    ],
  },
  {
    title: 'Tools & reference',
    links: [
      { id: 'try-it', label: 'Try it', target: 'try-it' },
      { id: 'code-examples', label: 'Code examples', target: 'code-examples' },
      { id: 'test-cards', label: 'Test cards', target: 'test-cards' },
      { id: 'errors', label: 'Error codes', target: 'errors' },
      { id: 'mistakes', label: 'Common mistakes', target: 'mistakes' },
      { id: 'notes', label: 'Integrator notes', target: 'notes' },
    ],
  },
];

const EXPLORE_CARDS = [
  {
    id: 'signature' as const,
    title: 'Add request signing to your server',
    description:
      'Learn how to build the HMAC-SHA256 signature every direct API call requires.',
    icon: BsShieldLock,
    gradient: 'sky',
  },
  {
    id: 'checkout' as const,
    title: 'Add hosted checkout to your app',
    description:
      'Redirect customers to a secure MoneyEU checkout page or embed it in an iframe.',
    icon: BsLayers,
    gradient: 'violet',
  },
  {
    id: 'status-webhook' as const,
    title: 'Listen for payment status webhooks',
    description:
      'Receive server-to-server notifications when a transaction succeeds or is declined.',
    icon: BsLink45Deg,
    gradient: 'mint',
  },
];

const PublicDocsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<NavSectionId>('overview');
  const [homeActive, setHomeActive] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const flatLinks = useMemo(
    () => SIDEBAR_GROUPS.flatMap((group) => group.links),
    [],
  );

  const scrollToTarget = useCallback((target: 'home' | NavSectionId) => {
    if (target === 'home') {
      document.getElementById('pdoc-home')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHomeActive(true);
      return;
    }
    setHomeActive(false);
    document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleActiveSectionChange = useCallback((sectionId: NavSectionId) => {
    setActiveSection(sectionId);
    const homeEl = document.getElementById('pdoc-home');
    if (homeEl) {
      const homeBottom = homeEl.getBoundingClientRect().bottom;
      setHomeActive(homeBottom > 120);
    }
  }, []);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;

    const sectionMatch = NAV_SECTIONS.find((section) =>
      section.label.toLowerCase().includes(query),
    );
    if (sectionMatch) {
      scrollToTarget(sectionMatch.id);
      return;
    }

    const linkMatch = flatLinks.find((link) => link.label.toLowerCase().includes(query));
    if (linkMatch) {
      scrollToTarget(linkMatch.target);
    }
  };

  const isLinkActive = (link: SidebarLink) => {
    if (link.target === 'home') return homeActive;
    return !homeActive && activeSection === link.target;
  };

  return (
    <div className="pdoc-page font-sans" data-theme="light">
      <aside className="pdoc-sidebar" aria-label="Documentation">
        <Link to="/docs" className="pdoc-sidebar-brand">
          <img src={logo} alt="" className="pdoc-sidebar-logo" />
        </Link>

        <nav className="pdoc-sidebar-nav">
          {SIDEBAR_GROUPS.map((group) => (
            <div key={group.title ?? 'home'} className="pdoc-sidebar-group">
              {group.title && <p className="pdoc-sidebar-group-title">{group.title}</p>}
              <ul>
                {group.links.map((link) => {
                  const Icon = link.icon;
                  return (
                    <li key={link.id}>
                      <button
                        type="button"
                        className={`pdoc-sidebar-link${isLinkActive(link) ? ' is-active' : ''}`}
                        onClick={() => scrollToTarget(link.target)}
                      >
                        {Icon && <Icon aria-hidden />}
                        {link.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <a
          href="https://moneyeu.com"
          className="pdoc-sidebar-visit"
          target="_blank"
          rel="noopener noreferrer"
        >
          Visit site
          <BsBoxArrowUpRight aria-hidden />
        </a>
      </aside>

      <div className="pdoc-main-column">
        <header className="pdoc-topbar">
          <div className="pdoc-search-wrap">
            <BsSearch className="pdoc-search-icon" aria-hidden />
            <input
              type="search"
              className="pdoc-search"
              placeholder="Search documentation…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search documentation"
            />
            <kbd className="pdoc-search-kbd">/</kbd>
          </div>
          <div className="pdoc-topbar-actions">
            {/* <a href="mailto:support@moneyeu.com" className="pdoc-feedback">
              Feedback about this page?
            </a> */}
            <Link to="/login" className="pdoc-sign-in">
              Sign in
            </Link>
          </div>
        </header>

        <div className="pdoc-content-scroll">
          <section id="pdoc-home" className="pdoc-home">
            <header className="pdoc-welcome">
              <h1>Welcome to MoneyEU</h1>
              <p>Let&apos;s get your payment integration production-ready.</p>
            </header>

            <article className="pdoc-featured">
              <div className="pdoc-featured-copy">
                <h2>Add payment processing to your app</h2>
                <p>
                  Start with credentials and HMAC signing, then call{' '}
                  <code>POST /v1/processPayment</code> from your backend. Use the interactive
                  playground to generate a signed cURL in seconds.
                </p>
                <button
                  type="button"
                  className="pdoc-get-started"
                  onClick={() => scrollToTarget('signature')}
                >
                  Get started
                  <BsArrowRight aria-hidden />
                </button>
              </div>
              <div className="pdoc-featured-art" aria-hidden>
                <div className="pdoc-cube pdoc-cube--1" />
                <div className="pdoc-cube pdoc-cube--2" />
                <div className="pdoc-cube pdoc-cube--3" />
                <BsCreditCard2Front className="pdoc-featured-icon" />
              </div>
            </article>

            <h3 className="pdoc-explore-title">Or, explore other documentation</h3>
            <div className="pdoc-explore-grid">
              {EXPLORE_CARDS.map(({ id, title, description, icon: Icon, gradient }) => (
                <button
                  key={id}
                  type="button"
                  className="pdoc-explore-card"
                  onClick={() => scrollToTarget(id)}
                >
                  <div className={`pdoc-explore-visual pdoc-explore-visual--${gradient}`}>
                    <Icon aria-hidden />
                  </div>
                  <div className="pdoc-explore-body">
                    <h4>{title}</h4>
                    <p>{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="pdoc-reference" aria-label="API reference">
            <div className="pdoc-public-context">
              <ApiDocumentation
                hideInPageNav
                onActiveSectionChange={handleActiveSectionChange}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PublicDocsPage;
